import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { Dice1 } from 'lucide-react';

const LANGUAGE_OPTIONS = ['ko', 'en', 'ja', 'zh'];

const convertDateToQtYmd = (value) => (value ? String(value).replace(/-/g, '').slice(0, 8) : '');

/** bible_book_aliases 조인 + language_code 조건으로 책 목록 조회 */
const fetchBibleBooksByLanguage = async (languageCode) => {
    const lang = (languageCode || 'ko').toLowerCase();
    let query = supabase
        .from('bible_books')
        .select(`
            code,
            display_name,
            sort_order,
            bible_book_aliases!inner (
                alias_name,
                language_code,
                is_primary
            )
        `)
        .eq('bible_book_aliases.is_primary', true)
        .order('sort_order', { ascending: true });

    if (lang === 'ko') {
        query = query.or('language_code.eq.ko,language_code.is.null', {
            referencedTable: 'bible_book_aliases',
        });
    } else {
        query = query.eq('bible_book_aliases.language_code', lang);
    }

    return query;
};

const mapBookRowWithAlias = (book, languageCode) => {
    const lang = (languageCode || 'ko').toLowerCase();
    const aliases = book.bible_book_aliases ?? [];
    const codeNorm = (c) => (c ?? '').toLowerCase();
    const matched =
        aliases.find((a) => codeNorm(a.language_code) === lang) ||
        (lang === 'ko' ? aliases.find((a) => a.language_code == null || a.language_code === '') : null) ||
        aliases[0];

    return {
        code: book.code,
        display_name: matched?.alias_name?.trim() || book.display_name,
        sort_order: book.sort_order,
    };
};

const DailyScriptureForm = () => {
    const { id } = useParams(); // 라우터에서 id 파라미터 추출
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        date: '',
        title: '',
        reference: '',
        commentary: '',
        question: '',
        is_active: 'N',
        country_cd: 'KR',
        group_cd: '100',
        language: 'ko',
        qt_ymd: '',
        daily_insight: '',
        dictionary: '',
    });
    const [loading, setLoading] = useState(false);
    const [scriptureVerses, setScriptureVerses] = useState([]);
    const [savingVerseIds, setSavingVerseIds] = useState(new Set());
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [qtAudioUrl, setQtAudioUrl] = useState('');
    const [qtAudioName, setQtAudioName] = useState('');
    const [bibleVersions, setBibleVersions] = useState([]);
    const [bibleBooks, setBibleBooks] = useState([]);
    const [rangeInput, setRangeInput] = useState({
        version_code: '',
        book_code: '',
        start_chapter: '',
        start_verse: '',
        end_chapter: '',
        end_verse: '',
    });
    const [addingRange, setAddingRange] = useState(false);
    const [showReplaceConfirmModal, setShowReplaceConfirmModal] = useState(false);
    const audioRef = useRef(null);
    const isVerseRangeInputComplete =
        Number(rangeInput.start_chapter) > 0 &&
        Number(rangeInput.start_verse) > 0 &&
        Number(rangeInput.end_chapter) > 0 &&
        Number(rangeInput.end_verse) > 0;

    const handleAudioPlay = async () => {
        if (!audioRef.current || !qtAudioUrl) return;
        try {
            await audioRef.current.play();
        } catch (error) {
            alert('오디오 재생에 실패했습니다.');
        }
    };

    const handleAudioPause = () => {
        if (!audioRef.current) return;
        audioRef.current.pause();
    };

    const handleAudioStop = () => {
        if (!audioRef.current) return;
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
    };

    // id가 있으면 해당 데이터를 조회해서 formData에 세팅
    useEffect(() => {
        const fetchScripture = async () => {
            if (!id || id === 'new') {
                setScriptureVerses([]);
                setQtAudioUrl('');
                setQtAudioName('');
                return;
            }
            setLoading(true);
            const { data, error } = await supabase
                .from('daily_scriptures')
                .select('*')
                .eq('id', id)
                .single();
            if (!error && data) {
                setFormData({
                    date: data.date || '',
                    title: data.title || '',
                    reference: data.reference || '',
                    commentary: data.commentary || '',
                    question: data.question || '',
                    is_active: data.is_active || 'N',
                    country_cd: data.country_cd || 'KR',
                    group_cd: data.group_cd || '100',
                    language: data.language || 'ko',
                    qt_ymd: data.qt_ymd || '',
                    daily_insight: data.daily_insight || '',
                    dictionary: data.dictionary || '',
                });
                setQtAudioUrl(data.qt_audio_url || '');
                setQtAudioName(data.qt_audio_name || '');

                // scripture_verses 조회
                const { data: versesData, error: versesError } = await supabase
                    .from('scripture_verses')
                    .select('*')
                    .eq('daily_scripture_id', id)
                    .order('chapter_number', { ascending: true })
                    .order('verse_number', { ascending: true })
                    .order('order_index', { ascending: true });

                if (!versesError && versesData) {
                    setScriptureVerses(versesData);
                }
            }
            setLoading(false);
        };
        fetchScripture();
    }, [id]);

    useEffect(() => {
        const fetchBibleVersions = async () => {
            const { data: versionsData, error: versionsError } = await supabase
                .from('bible_versions')
                .select('code, name, is_active')
                .eq('is_active', true)
                .order('code', { ascending: true });

            if (!versionsError && versionsData) {
                setBibleVersions(versionsData);
                setRangeInput((prev) => ({
                    ...prev,
                    version_code: prev.version_code || versionsData[0]?.code || '',
                }));
            }
        };

        fetchBibleVersions();
    }, []);

    useEffect(() => {
        const fetchBibleBooks = async () => {
            const languageCode = formData.language || 'ko';
            const { data: booksData, error: booksError } = await fetchBibleBooksByLanguage(languageCode);

            if (!booksError && booksData) {
                const mappedBooks = booksData.map((book) => mapBookRowWithAlias(book, languageCode));
                setBibleBooks(mappedBooks);
                setRangeInput((prev) => {
                    const currentStillValid = mappedBooks.some((book) => book.code === prev.book_code);
                    return {
                        ...prev,
                        book_code: currentStillValid ? prev.book_code : (mappedBooks[0]?.code || ''),
                    };
                });
            }
        };

        fetchBibleBooks();
    }, [formData.language]);

    const handleChange = (event) => {
        const { name, value, checked, type } = event.target;
        setFormData(prevState => {
            const nextValue = type === 'checkbox' ? (checked ? 'Y' : 'N') : value;
            const nextState = {
                ...prevState,
                [name]: nextValue,
            };

            if (name === 'date') {
                nextState.qt_ymd = convertDateToQtYmd(String(value || ''));
            }

            return nextState;
        });
    };

    const handleVerseChange = (verseId, field, value) => {
        setScriptureVerses(prevVerses =>
            prevVerses.map(verse =>
                verse.id === verseId ? { ...verse, [field]: value } : verse
            )
        );
    };

    const handleRangeInputChange = (event) => {
        const { name, value } = event.target;
        setRangeInput((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleAddVerse = () => {
        const newVerse = {
            id: `temp-${Date.now()}`,
            daily_scripture_id: id && id !== 'new' ? id : null,
            verse_number: scriptureVerses.length > 0 
                ? Math.max(...scriptureVerses.map(v => v.verse_number || 0)) + 1 
                : 1,
            verse_text: '',
            order_index: scriptureVerses.length,
        };
        setScriptureVerses([...scriptureVerses, newVerse]);
    };

    const handleAddVerseRange = async () => {
        const startChapter = Number(rangeInput.start_chapter);
        const startVerse = Number(rangeInput.start_verse);
        const endChapter = Number(rangeInput.end_chapter);
        const endVerse = Number(rangeInput.end_verse);

        if (!rangeInput.version_code || !rangeInput.book_code) {
            alert('성경 버전과 책을 선택해주세요.');
            return;
        }
        if (!startChapter || !startVerse || !endChapter || !endVerse) {
            alert('시작/종료 장절을 모두 입력해주세요.');
            return;
        }
        if (startChapter > endChapter || (startChapter === endChapter && startVerse > endVerse)) {
            alert('시작 장절이 종료 장절보다 클 수 없습니다.');
            return;
        }

        setAddingRange(true);
        try {
            const { data: verseRows, error } = await supabase
                .from('bible_version_verses')
                .select('chapter_number, verse_number, verse_text')
                .eq('version_code', rangeInput.version_code)
                .eq('book_code', rangeInput.book_code)
                .gte('chapter_number', startChapter)
                .lte('chapter_number', endChapter)
                .order('chapter_number', { ascending: true })
                .order('verse_number', { ascending: true });

            if (error) throw error;

            const normalizedRows = (verseRows || []).filter((row) => {
                if (row.chapter_number === startChapter && row.verse_number < startVerse) return false;
                if (row.chapter_number === endChapter && row.verse_number > endVerse) return false;
                return true;
            });

            if (normalizedRows.length === 0) {
                alert('해당 범위의 성경 구절을 찾을 수 없습니다.');
                return;
            }

            const selectedBook = bibleBooks.find((book) => book.code === rangeInput.book_code);
            const currentMaxOrderIndex = scriptureVerses.length > 0
                ? Math.max(...scriptureVerses.map((v) => Number(v.order_index) || 0))
                : -1;

            const generatedVerses = normalizedRows.map((row, index) => ({
                id: `temp-${Date.now()}-${index}`,
                daily_scripture_id: id && id !== 'new' ? id : null,
                verse_number: row.verse_number,
                verse_text: row.verse_text,
                chapter_number: row.chapter_number,
                book_name: selectedBook?.display_name || rangeInput.book_code,
                version_code: rangeInput.version_code,
                order_index: currentMaxOrderIndex + index + 1,
            }));

            if (id && id !== 'new') {
                const { error: deleteError } = await supabase
                    .from('scripture_verses')
                    .delete()
                    .eq('daily_scripture_id', id);

                if (deleteError) throw deleteError;
            }

            setScriptureVerses(generatedVerses);
            alert(`${generatedVerses.length}개의 구절이 새로 입력되었습니다.`);
        } catch (error) {
            console.error('성경 구절 범위 추가 오류:', error);
            alert(`구절 추가 중 오류가 발생했습니다: ${error.message}`);
        } finally {
            setAddingRange(false);
        }
    };

    const handleRequestAddVerseRange = () => {
        if ((scriptureVerses || []).length > 0) {
            setShowReplaceConfirmModal(true);
            return;
        }
        handleAddVerseRange();
    };

    const handleConfirmReplaceVerses = async () => {
        setShowReplaceConfirmModal(false);
        await handleAddVerseRange();
    };

    const handleDeleteAllVerses = async () => {
        if ((scriptureVerses || []).length === 0) {
            alert('삭제할 구절이 없습니다.');
            return;
        }

        if (!window.confirm('현재 등록된 구절을 모두 삭제하시겠습니까?')) {
            return;
        }
        console.log('id', id);
        setAddingRange(true);
        try {
            if (id && id !== 'new') {
                const { error } = await supabase
                    .from('scripture_verses')
                    .delete()
                    .eq('daily_scripture_id', id);

                if (error) throw error;
            }

            setScriptureVerses([]);
            alert('전체 구절이 삭제되었습니다.');
        } catch (error) {
            console.error('전체 구절 삭제 오류:', error);
            alert(`전체 구절 삭제 중 오류가 발생했습니다: ${error.message}`);
        } finally {
            setAddingRange(false);
        }
    };

    const handleDeleteVerse = async (verseId) => {
        // 임시 ID인 경우 (아직 저장되지 않은 구절)
        if (verseId.toString().startsWith('temp-')) {
            setScriptureVerses(prevVerses => prevVerses.filter(verse => verse.id !== verseId));
            return;
        }

        // 확인 메시지
        if (!window.confirm('이 구절을 삭제하시겠습니까?')) {
            return;
        }

        try {
            const { error } = await supabase
                .from('scripture_verses')
                .delete()
                .eq('id', verseId);

            if (error) throw error;

            setScriptureVerses(prevVerses => prevVerses.filter(verse => verse.id !== verseId));
            alert('구절이 삭제되었습니다.');
        } catch (error) {
            console.error('구절 삭제 오류:', error);
            alert(`구절 삭제 중 오류가 발생했습니다: ${error.message}`);
        }
    };

    const handleSaveVerse = async (verse) => {
        // 신규 입력 시에는 daily_scriptures를 먼저 저장해야 함
        if (!id || id === 'new') {
            alert('먼저 큐티 본문을 저장해주세요. 저장 후 성경 구절을 개별 저장할 수 있습니다.');
            return;
        }

        if (!verse.verse_text || verse.verse_text.trim() === '') {
            alert('성경 구절 내용을 입력해주세요.');
            return;
        }

        if (!verse.verse_number || verse.verse_number < 1) {
            alert('절 번호를 올바르게 입력해주세요.');
            return;
        }

        setSavingVerseIds(prev => new Set(prev).add(verse.id));
        try {
            const verseData = {
                daily_scripture_id: id,
                verse_number: verse.verse_number,
                verse_text: verse.verse_text,
                order_index: verse.order_index || 0,
                book_name: verse.book_name || null,
                chapter_number: verse.chapter_number || null,
                // language: formData.language,
                // qt_ymd: formData.qt_ymd,
            };

            // 임시 ID인 경우 (새로 추가된 구절)
            if (verse.id.toString().startsWith('temp-')) {
                const { data, error } = await supabase
                    .from('scripture_verses')
                    .insert([verseData])
                    .select()
                    .single();

                if (error) throw error;

                // 저장된 구절로 업데이트
                setScriptureVerses(prevVerses =>
                    prevVerses.map(v => v.id === verse.id ? { ...data } : v)
                );
            } else {
                // 기존 구절 업데이트
                const { error } = await supabase
                    .from('scripture_verses')
                    .update(verseData)
                    .eq('id', verse.id);

                if (error) throw error;
            }

            alert('구절이 저장되었습니다!');
        } catch (error) {
            console.error('구절 저장 오류:', error);
            alert(`구절 저장 중 오류가 발생했습니다: ${error.message}`);
        } finally {
            setSavingVerseIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(verse.id);
                return newSet;
            });
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        try {
            let savedId = id;
            if (id && id !== 'new') {
                // id가 있고 'new'가 아니면 update
                const { error } = await supabase
                    .from('daily_scriptures')
                    .update(formData)
                    .eq('id', id);
                if (error) throw error;
            } else {
                // id가 없거나 'new'이면 insert
                const { data, error } = await supabase
                    .from('daily_scriptures')
                    .insert([formData])
                    .select()
                    .single();
                if (error) throw error;
                savedId = data.id;
            }

            // 임시 성경 구절이 있으면 저장
            const tempVerses = scriptureVerses.filter(v => v.id.toString().startsWith('temp-'));
            if (tempVerses.length > 0) {
                const versesToSave = tempVerses
                    .filter(verse => verse.verse_text && verse.verse_text.trim() !== '')
                    .map((verse, index) => ({
                        daily_scripture_id: savedId,
                        verse_number: verse.verse_number,
                        verse_text: verse.verse_text,
                        order_index: verse.order_index || index,
                        book_name: verse.book_name || null,
                        chapter_number: verse.chapter_number || null,
                        // language: formData.language,
                        // qt_ymd: formData.qt_ymd,
                    }));

                if (versesToSave.length > 0) {
                    const { data: versesData, error: versesError } = await supabase
                        .from('scripture_verses')
                        .insert(versesToSave)
                        .select();

                    if (versesError) throw versesError;

                    // 저장된 구절로 업데이트
                    setScriptureVerses(prevVerses => {
                        const savedVerses = prevVerses.filter(v => !v.id.toString().startsWith('temp-'));
                        return [...savedVerses, ...versesData];
                    });
                }
            }

            alert('큐티 본문이 저장되었습니다!');
            
            // 신규 입력인 경우 페이지를 새로고침하여 id를 업데이트
            if (!id || id === 'new') {
                window.location.href = `/qt/DailyScriptureForm/${savedId}`;
            } else {
                // 수정인 경우 성경 구절 다시 조회
                const { data: versesData, error: versesError } = await supabase
                    .from('scripture_verses')
                    .select('*')
                    .eq('daily_scripture_id', savedId)
                    .order('chapter_number', { ascending: true })
                    .order('verse_number', { ascending: true })
                    .order('order_index', { ascending: true });

                if (!versesError && versesData) {
                    setScriptureVerses(versesData);
                }
            }
        } catch (error) {
            alert(`저장 중 오류가 발생했습니다: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card shadow mb-4">
            <div className="card-header py-3 d-flex align-items-center justify-content-between">
                <h6 className="m-0 font-weight-bold text-primary">큐티 본문 입력</h6>
                <div className="d-flex align-items-center">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary mr-2"
                        onClick={() => setShowPreviewModal(true)}
                    >
                        미리보기
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => navigate('/qt/DailyScriptureList')}
                    >
                        목록으로
                    </button>
                </div>
            </div>
            <div className="card-body">
                <form onSubmit={handleSubmit}>
                    <div className="form-group row">
                        <div className="col-sm-6 mb-3 mb-sm-0">
                            <label>날짜 (YYYY-MM-DD)</label>
                            <input type="date" className="form-control" name="date" value={formData.date} onChange={handleChange} required />
                        </div>
                        <div className="col-sm-6">
                            <label>제목</label>
                            <input type="text" className="form-control" name="title" value={formData.title} onChange={handleChange} required />
                        </div>
                        <div className="col-sm-6 mb-3 mb-sm-0">
                            <label>성경 구절 참조</label>
                            <input type="text" className="form-control" name="reference" value={formData.reference} onChange={handleChange} required />
                        </div>
                        <div className="col-sm-6">
                            <label>언어 코드</label>
                            <select className="form-control" name="language" value={formData.language} onChange={handleChange} required>
                                {LANGUAGE_OPTIONS.map((language) => (
                                    <option key={language} value={language}>
                                        {language}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-sm-6" style={{ display: 'none' }}>
                            <label>qt_ymd (YYYYMMDD)</label>
                            <input
                                type="text"
                                className="form-control"
                                name="qt_ymd"
                                value={formData.qt_ymd}
                                onChange={handleChange}
                                placeholder="예: 20260430"
                                maxLength={8}
                                required
                            />
                        </div>
                    </div>
                    
                    
                    <div className="form-group row">
                        <div className="col-sm-6">
                        
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="mb-0">성경 구절</label>
                                {/* <button
                                    type="button"
                                    className="btn btn-sm btn-success"
                                    onClick={handleAddVerse}
                                >
                                    추가
                                </button> */}
                            </div>
                            <div className="border rounded p-3 mb-2 bg-white">
                                <label className="mb-2 font-weight-bold">성경 범위로 추가</label>
                                <div className="form-row align-items-end">
                                    <div className="col-sm-3 mb-2">
                                        <label className="mb-1">성경 버전</label>
                                        <select
                                            className="form-control form-control-sm"
                                            name="version_code"
                                            value={rangeInput.version_code}
                                            onChange={handleRangeInputChange}
                                        >
                                            {bibleVersions.map((version) => (
                                                <option key={version.code} value={version.code}>
                                                    {version.code} ({version.name})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-sm-3 mb-2">
                                        <label className="mb-1">책</label>
                                        <select
                                            className="form-control form-control-sm"
                                            name="book_code"
                                            value={rangeInput.book_code}
                                            onChange={handleRangeInputChange}
                                        >
                                            {bibleBooks.map((book) => (
                                                <option key={book.code} value={book.code}>
                                                    {book.display_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-sm-2 mb-2">
                                        <label className="mb-1">시작 장</label>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            name="start_chapter"
                                            value={rangeInput.start_chapter}
                                            onChange={handleRangeInputChange}
                                            min="1"
                                        />
                                    </div>
                                    <div className="col-sm-2 mb-2">
                                        <label className="mb-1">시작 절</label>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            name="start_verse"
                                            value={rangeInput.start_verse}
                                            onChange={handleRangeInputChange}
                                            min="1"
                                        />
                                    </div>
                                    <div className="col-sm-2 mb-2">
                                        <label className="mb-1">종료 장</label>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            name="end_chapter"
                                            value={rangeInput.end_chapter}
                                            onChange={handleRangeInputChange}
                                            min="1"
                                        />
                                    </div>
                                    <div className="col-sm-2 mb-2">
                                        <label className="mb-1">종료 절</label>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            name="end_verse"
                                            value={rangeInput.end_verse}
                                            onChange={handleRangeInputChange}
                                            min="1"
                                        />
                                    </div>
                                    <div className="col-sm-3 mb-2">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-success btn-block font-weight-bold text-white"
                                            onClick={handleRequestAddVerseRange}
                                            disabled={addingRange || !isVerseRangeInputComplete}
                                        >
                                            {addingRange ? '추가 중...' : '구절 전체 추가'}
                                        </button>
                                    </div>
                                    <div className="col-sm-3 mb-2">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger btn-block font-weight-bold"
                                            onClick={handleDeleteAllVerses}
                                            disabled={addingRange || (scriptureVerses || []).length === 0}
                                        >
                                            구절 전체 삭제
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="border rounded p-3" 
                                style={{ 
                                    minHeight: '300px', 
                                    maxHeight: '500px', 
                                    overflowY: 'auto', 
                                    backgroundColor: '#f8f9fa', 
                                    marginBottom: '10px',
                                    padding: '10px'
                                }}>
                                {scriptureVerses.length === 0 ? (
                                    <p className="text-muted mb-0">성경 구절이 없습니다. 추가 버튼을 클릭하여 구절을 추가하세요.</p>
                                ) : (
                                    <div className="list-group">
                                        {scriptureVerses.map((verse, index) => (
                                            <div key={verse.id} className="list-group-item mb-2">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div className="d-flex align-items-center">
                                                        <label className="font-weight-bold text-primary mb-0 mr-2">
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm d-inline-block"
                                                                style={{ width: '60px' }}
                                                                value={verse.verse_number || ''}
                                                                onChange={(e) => handleVerseChange(verse.id, 'verse_number', parseInt(e.target.value) || 0)}
                                                                min="1"
                                                            />
                                                            절
                                                        </label>
                                                    </div>
                                                    <div>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-primary mr-2"
                                                            onClick={() => handleSaveVerse(verse)}
                                                            disabled={!id || id === 'new' || savingVerseIds.has(verse.id)}
                                                        >
                                                            {savingVerseIds.has(verse.id) ? '저장 중...' : '저장'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => handleDeleteVerse(verse.id)}
                                                            disabled={savingVerseIds.has(verse.id)}
                                                        >
                                                            삭제
                                                        </button>
                                                    </div>
                                                </div>
                                                <textarea
                                                    className="form-control"
                                                    rows="3"
                                                    value={verse.verse_text || ''}
                                                    onChange={(e) => handleVerseChange(verse.id, 'verse_text', e.target.value)}
                                                    placeholder="성경 구절 내용을 입력하세요"
                                                />
                                                {(verse.chapter_number || verse.book_name || verse.version_code) && (
                                                    <small className="text-muted d-block mt-2">
                                                        {verse.version_code ? `${verse.version_code} ` : ''}
                                                        {verse.book_name ? `${verse.book_name} ` : ''}
                                                        {verse.chapter_number ? `${verse.chapter_number}장 ` : ''}
                                                        {verse.verse_number ? `${verse.verse_number}절` : ''}
                                                    </small>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="mt-3 d-flex justify-content-between align-items-center">
                                    <label className="mb-0">매핑된 음성 파일</label>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-info"
                                        onClick={() => navigate('/qt/audios')}
                                    >
                                        음성 파일 관리
                                    </button>
                                </div>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={qtAudioName || '없음'}
                                    readOnly
                                />
                                <audio ref={audioRef} src={qtAudioUrl || undefined} />
                                <div className="mt-2 d-flex">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary mr-2"
                                        onClick={handleAudioPlay}
                                        disabled={!qtAudioUrl}
                                    >
                                        음성듣기
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary mr-2"
                                        onClick={handleAudioPause}
                                        disabled={!qtAudioUrl}
                                    >
                                        일시정지
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={handleAudioStop}
                                        disabled={!qtAudioUrl}
                                    >
                                        멈춤
                                    </button>
                                </div>
                        </div>
                        <div className="col-sm-6">
                            <div className="form-group">
                            <label>해설</label>
                            <textarea 
                                className="form-control" 
                                name="commentary" 
                                value={formData.commentary} 
                                onChange={handleChange} 
                                rows="12"
                                style={{ minHeight: '300px' }}
                            ></textarea>
                            
                                <label>Daily Insight</label>
                                <textarea 
                                    className="form-control" 
                                    name="daily_insight" 
                                    value={formData.daily_insight} 
                                    onChange={handleChange} 
                                    required />

                                <label className="mt-3">Question</label>
                                <textarea
                                    className="form-control"
                                    name="question"
                                    value={formData.question}
                                    onChange={handleChange}
                                    rows="4"
                                    placeholder="질문 내용을 입력하세요"
                                />
                                <label className="mt-3">Dictionary</label>
                                <textarea
                                    className="form-control"
                                    name="dictionary"
                                    value={formData.dictionary}
                                    onChange={handleChange}
                                    rows="4"
                                    placeholder="사전 내용을 입력하세요"
                                />
                                <div className="form-row mt-3">
                                    <div className="col-sm-4 mb-3 mb-sm-0">
                                        <label>국가 코드</label>
                                        <input type="text" className="form-control" name="country_cd" value={formData.country_cd} onChange={handleChange} />
                                    </div>
                                    <div className="col-sm-4 mb-3 mb-sm-0">
                                        <label>그룹 코드</label>
                                        <input type="text" className="form-control" name="group_cd" value={formData.group_cd} onChange={handleChange} />
                                    </div>
                                    <div className="col-sm-4 d-flex align-items-end">
                                        <div className="form-check mb-2">
                                            <input type="checkbox" className="form-check-input" name="is_active" checked={formData.is_active === 'Y'} onChange={handleChange} id="is_active_checkbox" />
                                            <label className="form-check-label" htmlFor="is_active_checkbox">활성화</label>
                                        </div>
                                    </div>
                                </div>
                                
                            </div>
                        </div>
                        
                    </div>
                    <div className="form-group row">
                        
                    </div>
                    <button type="submit" className="btn btn-primary btn-user btn-block" disabled={loading}>
                        {loading ? '저장 중...' : '저장하기'}
                    </button>
                </form>
            </div>

            {showPreviewModal && (
                <div
                    className="position-fixed"
                    style={{
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 1060,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '16px',
                    }}
                    onClick={() => setShowPreviewModal(false)}
                >
                    <div
                        className="bg-white rounded shadow"
                        style={{
                            width: '100%',
                            maxWidth: '760px',
                            maxHeight: '90vh',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div
                            className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom"
                            style={{ minHeight: '56px', backgroundColor: '#ffffff' }}
                        >
                            <strong style={{ fontSize: '1rem' }}>QT</strong>
                            <button
                                type="button"
                                className="btn btn-sm btn-light border"
                                onClick={() => setShowPreviewModal(false)}
                            >
                                닫기
                            </button>
                        </div>

                        <div className="p-3" style={{ overflowY: 'auto' }}>
                            <h6 className="text-muted mb-1">{formData.date || '날짜 없음'}</h6>
                            <h4 className="font-weight-bold mb-2">{formData.title || '제목 없음'}</h4>
                            <div className="mb-3 text-secondary">{formData.reference || '본문 구절 없음'}</div>

                            <div className="mb-4">
                                {(scriptureVerses || []).length === 0 ? (
                                    <p className="text-muted mb-0">등록된 성경 구절이 없습니다.</p>
                                ) : (
                                    scriptureVerses
                                        .slice()
                                        .sort((a, b) => {
                                            const chapterDiff = (a.chapter_number || 0) - (b.chapter_number || 0);
                                            if (chapterDiff !== 0) return chapterDiff;
                                            const verseDiff = (a.verse_number || 0) - (b.verse_number || 0);
                                            if (verseDiff !== 0) return verseDiff;
                                            return (a.order_index || 0) - (b.order_index || 0);
                                        })
                                        .map((verse) => (
                                            <div key={verse.id} className="mb-2 p-2 border rounded">
                                                <span className="font-weight-bold text-primary mr-2">
                                                    {verse.chapter_number ? `${verse.chapter_number}장 ` : ''}
                                                    {verse.verse_number || '-'}절
                                                </span>
                                                <span>{verse.verse_text || ''}</span>
                                            </div>
                                        ))
                                )}
                            </div>

                            <div className="mb-3">
                                <h6 className="font-weight-bold mb-2 text-primary">해설</h6>
                                <div className="border rounded p-3" style={{ whiteSpace: 'pre-line' }}>
                                    {formData.commentary || '해설 내용이 없습니다.'}
                                </div>
                            </div>

                            <div>
                                <h6 className="font-weight-bold mb-2 text-primary">Daily Insight</h6>
                                <div className="border rounded p-3" style={{ whiteSpace: 'pre-line' }}>
                                    {formData.daily_insight || 'Daily Insight 내용이 없습니다.'}
                                </div>
                            </div>

                            <div className="mt-3">
                                <h6 className="font-weight-bold mb-2 text-primary">Question</h6>
                                <div className="border rounded p-3" style={{ whiteSpace: 'pre-line' }}>
                                    {formData.question || 'Question 내용이 없습니다.'}
                                </div>
                            </div>

                            <div className="mt-3">
                                <h6 className="font-weight-bold mb-2 text-primary">성경사전</h6>
                                <div className="border rounded p-3" style={{ whiteSpace: 'pre-line' }}>
                                    {formData.dictionary || '성경사전에 내용이 없습니다.'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showReplaceConfirmModal && (
                <div
                    className="position-fixed"
                    style={{
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 1070,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '16px',
                    }}
                    onClick={() => setShowReplaceConfirmModal(false)}
                >
                    <div
                        className="bg-white rounded shadow p-3"
                        style={{ width: '100%', maxWidth: '460px' }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <h6 className="font-weight-bold mb-3">확인</h6>
                        <p className="mb-3">
                            범위 추가 버튼을 누르면 이전에 추가한 구절이 삭제되고 새로 입력 됩니다.
                            <br />
                            진행하시겠습니까?
                        </p>
                        <div className="d-flex justify-content-end">
                            <button
                                type="button"
                                className="btn btn-sm btn-secondary mr-2"
                                onClick={() => setShowReplaceConfirmModal(false)}
                            >
                                아니오
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={handleConfirmReplaceVerses}
                                disabled={addingRange}
                            >
                                예
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DailyScriptureForm;